// src/components/PersonRegistration.jsx
import React, { useState } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { registerPerson } from '../../features/personSlice';
import { validateFields } from '../../services/validate';
import { closeSnackbar, useSnackbar } from 'notistack';
import { Close as CloseIcon } from '@mui/icons-material';
import DynamicBreadcrumbs from '../layout/Breadcrumb';
import Layout from '../layout/Layout';

const PersonRegistration = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { crimeNumber, status } = useSelector((state) => state.person);

  const [formData, setFormData] = useState({
    aadharNumber: '',
    email: '',
    mobile: '',
    firstName: '',
    middleName: '',
    lastName: '',
    isFirstTimeOffender: true,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleCheckboxChange = (e) => {
    setFormData({ ...formData, isFirstTimeOffender: e.target.checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();


    try {
      const response = await dispatch(registerPerson(formData)).unwrap();
      enqueueSnackbar(response.message, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error || 'Failed to register person', {
        variant: 'error',
        action: (key) => (
          <IconButton onClick={() => closeSnackbar(key)} color="inherit">
            <CloseIcon />
          </IconButton>
        ),
      });
    }
  };

  return (
    <Layout title="Person Registration" description="Uttar Pradesh Police Crime Records">
      <Container maxWidth="md" sx={{ py: 4 }}>
        <DynamicBreadcrumbs />

        <Box
          sx={{
            bgcolor: '#f5f5f5',
            p: 4,
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 600 }}>
            Register Offender / Person
          </Typography>

          {crimeNumber && formData.isFirstTimeOffender && (
            <Box mt={3} p={2} bgcolor="success.light" borderRadius={2}>
              <Typography variant="h6" align="center" color="success.dark">
                Generated Crime Number: <strong>{crimeNumber}</strong>
              </Typography>
            </Box>
          )}

          <Typography variant="body1" align="center" color="text.secondary" sx={{ my: 3 }}>
            Register the person using Aadhaar. System will verify if this is their first offense.
          </Typography>

          <form onSubmit={handleSubmit}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isFirstTimeOffender}
                  onChange={handleCheckboxChange}
                  color="primary"
                />
              }
              label="Is this the person's first offense?"
              sx={{ mb: 2 }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Aadhaar Number"
                  name="aadharNumber"
                  value={formData.aadharNumber}
                  onChange={handleChange}
                  error={!!errors.aadharNumber}
                  helperText={errors.aadharNumber || '12 digits'}
                  inputProps={{ maxLength: 12 }}
                  placeholder="xxxxxxxxxxxx"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  error={!!errors.mobile}
                  helperText={errors.mobile || '10 digits'}
                  inputProps={{ maxLength: 10 }}
                  placeholder="xxxxxxxxxx"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Middle Name (Optional)"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  placeholder="example@domain.com"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
            </Grid>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={status === 'loading'}
              sx={{ mt: 4, py: 1.5 }}
            >
              {status === 'loading' ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 2 }} />
                  Registering...
                </>
              ) : (
                'Register Person'
              )}
            </Button>
          </form>
        </Box>
      </Container>
    </Layout>
  );
};

export default PersonRegistration;